import { useFetch } from './useFetch'
import useAlert from '../alert/useAlert'
import { AlertVariants } from '../../context/Alert/AlertContext'
import {
    PriceExtraction,
    GetExtractionsResponse,
    GetExtractionChangesResponse,
    GetExtractionStatusResponse,
    CreateExtractionResponse,
    ValidateChangesBody,
    ValidateChangesResponse,
    ApplyChangesResponse,
    MapUnitResponse,
    GetProjectsResponse,
    ExtractionType,
    ResolveDiscrepancyBody,
    ResolveDiscrepancyResponse
} from '../../interfaces/priceExtraction'

export const usePriceExtractionsFetch = () => {
    const { setTypeGlobalAlert, setShowGlobalAlert, setMessageGlobalAlert, setPositionAlert } = useAlert()
    const { authFetch } = useFetch()

    const BASE_URL = process.env.REACT_APP_AUTH_API_URL + '/api/admin/price-extractions'

    // Obtener lista de extracciones con paginacion y filtros
    const getExtractions = async (
        page: number,
        limit: number,
        country_id?: number,
        status?: string,
        date_from?: string,
        date_to?: string,
        project_id?: number,
        created_by?: string | number
    ): Promise<GetExtractionsResponse> => {
        let result: GetExtractionsResponse = {
            success: false,
            data: [],
            total: 0,
            page: 1,
            totalPages: 0,
            message: ''
        }

        try {
            const body: any = { page, limit }
            if (country_id) body.country_id = country_id
            if (status) body.status = status
            if (date_from) body.date_from = date_from
            if (date_to) body.date_to = date_to
            if (project_id) body.project_id = project_id
            if (created_by) body.created_by = created_by

            const response = await authFetch(`${BASE_URL}/list`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener extracciones'
            }
        } catch (error) {
            console.error('Error en getExtractions:', error)
        }

        return result
    }

    // Obtener detalle de una extraccion
    const getExtractionById = async (id: number): Promise<{ success: boolean; data: PriceExtraction | null; message: string }> => {
        let result = { success: false, data: null as PriceExtraction | null, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/detail/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener extraccion'
            }
        } catch (error) {
            console.error('Error en getExtractionById:', error)
        }

        return result
    }

    // Obtener status de una extraccion (para polling)
    const getExtractionStatus = async (id: number): Promise<GetExtractionStatusResponse> => {
        let result: GetExtractionStatusResponse = { success: false, data: null, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/status/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener status'
            }
        } catch (error) {
            console.error('Error en getExtractionStatus:', error)
        }

        return result
    }

    // Obtener cambios de una extraccion
    const getExtractionChanges = async (
        id: number,
        status_filter?: string,
        project_filter?: number,
        show_only_changes?: boolean
    ): Promise<GetExtractionChangesResponse> => {
        let result: GetExtractionChangesResponse = {
            success: false,
            data: [],
            extraction: {} as PriceExtraction,
            availableUnits: {},
            total: 0,
            message: ''
        }

        try {
            let url = `${BASE_URL}/changes/${id}?`
            if (status_filter && status_filter !== 'all') url += `status_filter=${status_filter}&`
            if (project_filter) url += `project_filter=${project_filter}&`
            if (show_only_changes) url += `show_only_changes=true&`

            const response = await authFetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener cambios'
            }
        } catch (error) {
            console.error('Error en getExtractionChanges:', error)
        }

        return result
    }

    // Crear nueva extraccion (soporta multiples archivos)
    const createExtraction = async (
        country_id: number,
        project_ids: number[],
        files: File[],
        extraction_type: ExtractionType,
        description?: string
    ): Promise<CreateExtractionResponse> => {
        let result: CreateExtractionResponse = { success: false, message: '' }

        try {
            const formData = new FormData()
            formData.append('country_id', country_id.toString())
            formData.append('project_ids', JSON.stringify(project_ids))
            formData.append('extraction_type', extraction_type)
            // Agregar cada archivo con el mismo nombre - Multer los agrupa en array
            files.forEach(file => {
                formData.append('priceListFiles', file)
            })
            if (description) formData.append('description', description)

            const response = await authFetch(`${BASE_URL}/create`, {
                method: 'POST',
                body: formData,
                headers: { 'Cache-Control': 'no-cache' }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al crear extraccion'
            }
        } catch (error) {
            console.error('Error en createExtraction:', error)
        }

        return result
    }

    // Validar cambios (aceptar/rechazar)
    const validateChanges = async (
        extractionId: number,
        body: ValidateChangesBody
    ): Promise<ValidateChangesResponse> => {
        let result: ValidateChangesResponse = { success: false, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/validate/${extractionId}`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al validar cambios'
            }
        } catch (error) {
            console.error('Error en validateChanges:', error)
        }

        return result
    }

    // Aplicar cambios a la BD
    const applyChanges = async (extractionId: number): Promise<ApplyChangesResponse> => {
        let result: ApplyChangesResponse = { success: false, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/apply/${extractionId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al aplicar cambios'
            }
        } catch (error) {
            console.error('Error en applyChanges:', error)
        }

        return result
    }

    // Mapear unidad manualmente
    const mapUnit = async (
        change_id: number,
        unit_id?: number,
        garage_id?: number
    ): Promise<MapUnitResponse> => {
        let result: MapUnitResponse = { success: false, message: '' }

        try {
            const body: any = { change_id }
            if (unit_id) body.unit_id = unit_id
            if (garage_id) body.garage_id = garage_id

            const response = await authFetch(`${BASE_URL}/map-unit`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al mapear unidad'
            }
        } catch (error) {
            console.error('Error en mapUnit:', error)
        }

        return result
    }

    // Resolver discrepancia (modo PRO)
    const resolveDiscrepancy = async (
        body: ResolveDiscrepancyBody
    ): Promise<ResolveDiscrepancyResponse> => {
        let result: ResolveDiscrepancyResponse = { success: false, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/resolve-discrepancy`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al resolver discrepancia'
            }
        } catch (error) {
            console.error('Error en resolveDiscrepancy:', error)
        }

        return result
    }

    // Obtener proyectos por pais
    const getProjectsByCountry = async (countryId: number): Promise<GetProjectsResponse> => {
        let result: GetProjectsResponse = { success: false, data: [], message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/projects-by-country/${countryId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener proyectos'
            }
        } catch (error) {
            console.error('Error en getProjectsByCountry:', error)
        }

        return result
    }

    // Obtener proyectos que tienen extracciones (para el filtro)
    const getProjectsWithExtractions = async (): Promise<GetProjectsResponse> => {
        let result: GetProjectsResponse = { success: false, data: [], message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/projects-with-extractions`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener proyectos'
            }
        } catch (error) {
            console.error('Error en getProjectsWithExtractions:', error)
        }

        return result
    }

    // Obtener creadores de extracciones (para el filtro)
    const getCreators = async (): Promise<{ success: boolean; data: { id: string | number; name: string }[]; message: string }> => {
        let result = { success: false, data: [] as { id: string | number; name: string }[], message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/creators`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al obtener creadores'
            }
        } catch (error) {
            console.error('Error en getCreators:', error)
        }

        return result
    }

    // Ejecutar extraccion desde Master Sheet (sin subir archivo)
    const runFromSheet = async (projectId: number): Promise<{
        success: boolean
        data?: {
            extractionId: number
            projectId: number
            projectName: string
            status: string
            totalUnits: number
            totalGarages: number
            changesPending: number
            matchedUnits: number
            unmatchedUnits: number
        }
        message: string
        errors?: string[]
    }> => {
        let result: any = { success: false, message: '' }

        try {
            const response = await authFetch(`${BASE_URL}/run-from-sheet`, {
                method: 'POST',
                body: JSON.stringify({ project_id: projectId }),
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })

            if (!response.error) {
                result = response
            } else {
                result.message = response.message || 'Error al ejecutar extraccion'
                result.errors = response.errors
            }
        } catch (error) {
            console.error('Error en runFromSheet:', error)
        }

        return result
    }

    // Mostrar alerta global
    const showAlertMessage = (type: AlertVariants, message: string) => {
        setPositionAlert('bottom')
        setTypeGlobalAlert(type)
        setMessageGlobalAlert(message)
        setShowGlobalAlert(true)
        setTimeout(() => {
            setShowGlobalAlert(false)
            setTypeGlobalAlert('success')
            setMessageGlobalAlert('')
            setPositionAlert('top')
        }, 3000)
    }

    return {
        getExtractions,
        getExtractionById,
        getExtractionStatus,
        getExtractionChanges,
        createExtraction,
        validateChanges,
        applyChanges,
        mapUnit,
        resolveDiscrepancy,
        getProjectsByCountry,
        getProjectsWithExtractions,
        getCreators,
        runFromSheet,
        showAlertMessage
    }
}
